/**
 * AppShell — the MERIDIAN application shell. Mounts the ONE Instrument Bezel and
 * the environment, globally and permanently, so every route is genuinely mounted
 * into the same continuous machined instrument (the bezel is never re-instantiated
 * per page). Layers, back → front:
 *   - MapSnapshotProvider (context: frozen-map snapshot + ambient env signals)
 *   - MapBackdrop (the environment — the map, fixed behind everything, inert)
 *   - InstrumentBezel (the one-glass L: fixed corner + top spine + collapsible
 *     left spine; the map does NOT tint it — it owns its own material)
 *   - Workspace (the recessed content module seated into the bezel opening; pages
 *     render their content here and no longer own any shell chrome)
 *   - SkipLink / RouteAnnouncer (a11y; target #main-content, provided by Workspace)
 *   - MobileBottomNav (≤768px, where the bezel is hidden)
 *
 * Pages are pure content. Global navigation lives in the bezel's left spine; the
 * global command center (search, live status, theme, user) lives in its top spine.
 */

import type { ReactNode } from 'react';
import { SkipLink } from '../shared/SkipLink';
import { MobileBottomNav } from './MobileBottomNav';
import { RouteAnnouncer } from './RouteAnnouncer';
import { MapBackdrop } from './MapBackdrop';
import { InstrumentBezel } from './InstrumentBezel';
import { Workspace } from './Workspace';
import { LiquidGlassSVG } from '../LiquidGlassSVG';
import { MapSnapshotProvider } from '../../providers/MapSnapshotProvider';
import { useAppStore } from '../../store/appStore';
import './AppShell.css';

interface AppShellProps {
  children: ReactNode;
}

export const AppShell = ({ children }: AppShellProps) => {
  // The collapse state lives on .app-shell — the COMMON ANCESTOR of both the bezel
  // and the workspace — so the single --ib-rail-w token resolves identically for
  // both. The bezel's left spine and the workspace's left inset therefore move in
  // exact lockstep on collapse (they are siblings; a token set only on the bezel
  // would not reach the workspace).
  const { isPanelCollapsed } = useAppStore();

  return (
    <MapSnapshotProvider>
      <div className={`app-shell${isPanelCollapsed ? ' shell-collapsed' : ''}`}>
        <LiquidGlassSVG />
        <MapBackdrop />
        <SkipLink />
        <RouteAnnouncer />
        <InstrumentBezel />
        <Workspace>{children}</Workspace>
        <MobileBottomNav />
      </div>
    </MapSnapshotProvider>
  );
};
